import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    FiChevronDown,
    FiChevronUp,
    FiAlertCircle,
    FiArrowLeft,
    FiPrinter,
    FiDollarSign,
    FiCreditCard,
    FiHome,
    FiPlus,
    FiTruck,
    FiShield,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import feeService from "../../services/feeService";
import PrintInstructions from "../../components/common/PrintInstructions";
import CollegePrintHeader from "../../components/common/CollegePrintHeader";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN")}`;

const formatStatementDate = (d = new Date()) => {
    const day = d.getDate();
    const month = d.toLocaleString("en-US", { month: "long" });
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
};

const formatYearSuffix = (n) => {
    const num = parseInt(n, 10);
    if (num === 1) return "1st Year";
    if (num === 2) return "2nd Year";
    if (num === 3) return "3rd Year";
    if (num === 4) return "4th Year";
    return `Year ${num}`;
};

// ── Official Statement Sheet Component (Printed Output) ───────────────────────
function OfficialStatementSheet({ student, yearData, generatedDate }) {
    if (!student || !yearData) return null;

    const feesDecided = Number(yearData.feesDecided || student.feesDecided || yearData.academic?.totalReceivables || 0);
    const scholarshipBalance = Number(yearData.scholarshipAmountBalance || student.scholarshipAmountBalance || yearData.academic?.scholarshipAmount || 0);
    const transactions = yearData.transactions || [];
    const totalPaid = transactions.reduce((sum, tx) => sum + Number(tx.amountPaid || 0), 0) || Number(yearData.academic?.totalCollected || 0);
    const netPayable = feesDecided - scholarshipBalance;
    const pendingBalance = Math.max(0, feesDecided - totalPaid);
    const balance = Math.max(0, netPayable - totalPaid);
    const isPaid = balance <= 0 && netPayable > 0;

    const courseNameStr = student.courseAdmitted || student.course?.courseName || student.course?.shortName || student.course || "Course";
    const courseYearStr = `${courseNameStr} — ${formatYearSuffix(yearData.yearNumber)}`;

    const college = student.college || {};
    const collegePhone = college.contactPhone || "0712-2537444";
    const collegeEmail = college.contactEmail || "cicopnagpur@gmail.com";

    const fullAddress = [
        student.address || student.parentAddress,
        student.city,
        student.state || "Maharashtra",
    ].filter(Boolean).join(", ");

    return (
        <div className="fee-statement-document" style={{
            background: "#fff",
            color: "#1e293b",
            fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            fontSize: "13px",
            lineHeight: "1.4",
            padding: "0.4in 0.4in 0.3in",
            margin: "0 auto",
            maxWidth: "8.2in",
            boxSizing: "border-box",
        }}>
            {/* ── College Header ── */}
            <CollegePrintHeader
                college={college}
                layout="banner"
                logoSize={64}
                showBorders={true}
            />

            {/* ── Statement Title & Status Row ── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px", flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h2 style={{ fontSize: "16px", fontWeight: 900, textDecoration: "underline", textUnderlineOffset: "4px", margin: 0, color: "#0f172a", letterSpacing: "0.4px" }}>
                        STUDENT FEE PAYMENT STATEMENT
                    </h2>
                    <div style={{ fontSize: "11.5px", color: "#475569", marginTop: "4px", fontWeight: 500 }}>
                        Academic Year: {yearData.academicYearLabel}
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748b" }}>
                        Generated: {generatedDate}
                    </div>
                </div>

                <div>
                    {isPaid ? (
                        <div style={{
                            background: "#dcfce7",
                            border: "1px solid #86efac",
                            color: "#15803d",
                            fontWeight: 800,
                            fontSize: "12px",
                            padding: "4px 12px",
                            borderRadius: "6px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            letterSpacing: "0.3px",
                        }}>
                            ✓ FULLY PAID
                        </div>
                    ) : (
                        <div style={{
                            background: "#fef9c3",
                            border: "1px solid #fde047",
                            color: "#92400e",
                            fontWeight: 800,
                            fontSize: "12px",
                            padding: "4px 12px",
                            borderRadius: "6px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            letterSpacing: "0.3px",
                        }}>
                            ⌛ BALANCE PENDING
                        </div>
                    )}
                </div>
            </div>

            {/* ── Student Information Box ── */}
            <div style={{
                background: "#fafafa",
                border: "1px solid #e2e8f0",
                borderRadius: "6px",
                padding: "12px 16px",
                marginBottom: "14px",
            }}>
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 1fr 1fr",
                    gap: "8px 16px",
                    fontSize: "12px",
                }}>
                    <div>
                        <span style={{ color: "#64748b", fontSize: "11.5px" }}>Student Name: </span>
                        <strong style={{ color: "#0f172a", textTransform: "uppercase" }}>{student.fullName}</strong>
                    </div>
                    <div>
                        <span style={{ color: "#64748b", fontSize: "11.5px" }}>Enrollment No.: </span>
                        <strong style={{ color: "#0f172a" }}>{student.enrollmentNo || "—"}</strong>
                    </div>
                    <div>
                        <span style={{ color: "#64748b", fontSize: "11.5px" }}>Course: </span>
                        <strong style={{ color: "#0f172a" }}>{courseYearStr}</strong>
                    </div>

                    <div>
                        <span style={{ color: "#64748b", fontSize: "11.5px" }}>Gender: </span>
                        <strong style={{ color: "#0f172a" }}>{student.gender || "—"}</strong>
                    </div>
                    <div>
                        <span style={{ color: "#64748b", fontSize: "11.5px" }}>Admission Category: </span>
                        <strong style={{ color: "#0f172a" }}>{student.admissionCategory || "Open"}</strong>
                    </div>
                    <div>
                        <span style={{ color: "#64748b", fontSize: "11.5px" }}>Admission Type: </span>
                        <strong style={{ color: "#0f172a" }}>{student.admissionType || "CAP"}</strong>
                    </div>

                    <div>
                        <span style={{ color: "#64748b", fontSize: "11.5px" }}>Father's Name: </span>
                        <strong style={{ color: "#0f172a", textTransform: "uppercase" }}>{student.fatherName || "—"}</strong>
                    </div>
                    <div>
                        <span style={{ color: "#64748b", fontSize: "11.5px" }}>Mobile: </span>
                        <strong style={{ color: "#0f172a" }}>{student.mobile || "—"}</strong>
                    </div>
                    <div>
                        <span style={{ color: "#64748b", fontSize: "11.5px" }}>Father's Mobile: </span>
                        <strong style={{ color: "#0f172a" }}>{student.fatherMobile || "—"}</strong>
                    </div>

                    <div style={{ gridColumn: "1 / -1", borderTop: "1px dashed #e2e8f0", paddingTop: "6px", marginTop: "2px" }}>
                        <span style={{ color: "#64748b", fontSize: "11.5px" }}>Address: </span>
                        <strong style={{ color: "#0f172a", textTransform: "uppercase" }}>{fullAddress || "—"}</strong>
                    </div>
                </div>
            </div>

            {/* ── 4 KPI Metric Cards in a Row ── */}
            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "10px",
                marginBottom: "16px",
            }}>
                <div style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "10px 8px",
                    textAlign: "center",
                    background: "#fff",
                }}>
                    <div style={{ fontSize: "17px", fontWeight: 900, color: "#003b67" }}>
                        {inr(feesDecided)}
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px", fontWeight: 600, textTransform: "uppercase" }}>
                        Fees Decided
                    </div>
                </div>

                <div style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "10px 8px",
                    textAlign: "center",
                    background: "#fff",
                }}>
                    <div style={{ fontSize: "17px", fontWeight: 900, color: "#0f766e" }}>
                        {inr(scholarshipBalance)}
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px", fontWeight: 600, textTransform: "uppercase" }}>
                        Scholarship Balance
                    </div>
                </div>

                <div style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "10px 8px",
                    textAlign: "center",
                    background: "#fff",
                }}>
                    <div style={{ fontSize: "17px", fontWeight: 900, color: "#15803d" }}>
                        {inr(totalPaid)}
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px", fontWeight: 600, textTransform: "uppercase" }}>
                        Total Paid
                    </div>
                </div>

                <div style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "10px 8px",
                    textAlign: "center",
                    background: "#fff",
                }}>
                    <div style={{ fontSize: "17px", fontWeight: 900, color: pendingBalance > 0 ? "#dc2626" : "#15803d" }}>
                        {inr(pendingBalance)}
                    </div>
                    <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px", fontWeight: 600, textTransform: "uppercase" }}>
                        Balance Pending
                    </div>
                </div>
            </div>

            {/* ── Fee Payment Details Table ── */}
            <div style={{ marginBottom: "14px" }}>
                <div style={{ fontSize: "13px", fontWeight: 800, color: "#0f172a", marginBottom: "6px" }}>
                    Fee Payment Details ({transactions.length} installment{transactions.length !== 1 ? "s" : ""})
                </div>

                <table style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "12px",
                    textAlign: "left",
                }}>
                    <thead>
                        <tr style={{ background: "#f8fafc", borderTop: "1.5px solid #cbd5e1", borderBottom: "1.5px solid #cbd5e1" }}>
                            <th style={{ padding: "7px 8px", textAlign: "center", width: "40px", color: "#475569", fontWeight: 700 }}>SR.</th>
                            <th style={{ padding: "7px 8px", width: "160px", color: "#475569", fontWeight: 700 }}>RECEIPT NO.</th>
                            <th style={{ padding: "7px 8px", color: "#475569", fontWeight: 700 }}>INSTALLMENT</th>
                            <th style={{ padding: "7px 8px", textAlign: "right", width: "120px", color: "#475569", fontWeight: 700 }}>AMOUNT PAID</th>
                            <th style={{ padding: "7px 8px", textAlign: "center", width: "95px", color: "#475569", fontWeight: 700 }}>DATE</th>
                            <th style={{ padding: "7px 8px", textAlign: "center", width: "80px", color: "#475569", fontWeight: 700 }}>MODE</th>
                            <th style={{ padding: "7px 8px", color: "#475569", fontWeight: 700 }}>REF / CHEQUE</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transactions.length === 0 ? (
                            <tr>
                                <td colSpan={7} style={{ textAlign: "center", padding: "1.25rem", color: "#64748b" }}>
                                    No fee payment receipts recorded for this academic year.
                                </td>
                            </tr>
                        ) : (
                            transactions.map((tx, idx) => (
                                <tr key={tx._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                    <td style={{ padding: "7px 8px", textAlign: "center", color: "#64748b" }}>{idx + 1}</td>
                                    <td style={{ padding: "7px 8px", fontWeight: 700, color: "#003b67", fontFamily: "monospace", fontSize: "12px" }}>
                                        {tx.receiptNo}
                                    </td>
                                    <td style={{ padding: "7px 8px" }}>
                                        <div style={{ fontWeight: 600, color: "#0f172a" }}>
                                            {tx.installmentLabel || `Installment #${tx.installmentNo}`}
                                        </div>
                                        {tx.remarks && (
                                            <div style={{ fontSize: "11px", color: "#64748b", marginTop: "1px" }}>
                                                {tx.remarks}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: "7px 8px", textAlign: "right", fontWeight: 800, color: "#16a34a" }}>
                                        {inr(tx.amountPaid)}
                                    </td>
                                    <td style={{ padding: "7px 8px", textAlign: "center", color: "#475569" }}>
                                        {tx.paymentDate}
                                    </td>
                                    <td style={{ padding: "7px 8px", textAlign: "center" }}>
                                        <span style={{
                                            fontSize: "11px",
                                            fontWeight: 600,
                                            color: tx.paymentMode === "Cash" ? "#15803d" : "#003b67",
                                        }}>
                                            {tx.paymentMode}
                                        </span>
                                    </td>
                                    <td style={{ padding: "7px 8px", color: "#475569", fontSize: "11.5px" }}>
                                        {tx.transactionRef ? `UTR No. ${tx.transactionRef}` : tx.chequeNo ? `Chq No. ${tx.chequeNo}` : "—"}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* ── Totals Summary Box (Right Aligned) ── */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "26px" }}>
                <div style={{
                    width: "340px",
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "6px",
                    padding: "10px 14px",
                    fontSize: "12.5px",
                }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                        <span style={{ color: "#475569" }}>Total Fees Decided for {yearData.academicYearLabel}:</span>
                        <strong style={{ color: "#0f172a" }}>{inr(feesDecided)}</strong>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                        <span style={{ color: "#475569" }}>Total Amount Received:</span>
                        <strong style={{ color: "#15803d" }}>{inr(totalPaid)}</strong>
                    </div>
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderTop: "1.5px solid #cbd5e1",
                        paddingTop: "6px",
                        fontSize: "13.5px",
                    }}>
                        <strong style={{ color: "#0f172a" }}>Balance Payable:</strong>
                        <strong style={{ color: pendingBalance > 0 ? "#dc2626" : "#15803d" }}>{inr(pendingBalance)}</strong>
                    </div>
                </div>
            </div>

            {/* ── Signature Block ── */}
            <div style={{
                marginTop: "32px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-end",
                padding: "0 10px",
            }}>
                <div style={{ textAlign: "center", minWidth: "160px" }}>
                    <div style={{ borderTop: "1.5px solid #0f172a", paddingTop: "5px", fontSize: "11.5px", fontWeight: 700, color: "#0f172a" }}>
                        Student / Parent Signature
                    </div>
                </div>

                <div style={{ textAlign: "center", minWidth: "160px" }}>
                    <div style={{ borderTop: "1.5px solid #0f172a", paddingTop: "5px", fontSize: "11.5px", fontWeight: 700, color: "#0f172a" }}>
                        Accounts Department
                    </div>
                </div>

                <div style={{ textAlign: "center", minWidth: "160px" }}>
                    <div style={{ borderTop: "1.5px solid #0f172a", paddingTop: "5px", fontSize: "11.5px", fontWeight: 700, color: "#0f172a" }}>
                        Principal — {college.name || "CICP, Nagpur"}
                    </div>
                </div>
            </div>

            {/* ── Footer ── */}
            <div style={{
                marginTop: "24px",
                paddingTop: "8px",
                borderTop: "1px dashed #cbd5e1",
                textAlign: "center",
                fontSize: "10px",
                color: "#64748b",
            }}>
                This is a computer-generated fee statement. For queries contact: {collegePhone} · {collegeEmail}
            </div>
        </div>
    );
}

// ── Main Page Component ────────────────────────────────────────────────────────
export default function FeeStudentStatement() {
    const { studentId } = useParams();
    const navigate = useNavigate();
    const { role } = useApp();
    const feeBasePath = role === "clerk" ? "/clerk/fees" : role === "accountant" ? "/accountant/fees" : "/fees";

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    // Selected year for printing
    const [selectedPrintYear, setSelectedPrintYear] = useState(null);

    // Tracks open/closed status for each year accordion
    const [openAccordions, setOpenAccordions] = useState({});
    // Tracks active sub-tab ('academic' | 'others' | 'transactions') per yearNumber
    const [activeTabs, setActiveTabs] = useState({});

    const generatedDate = useMemo(() => formatStatementDate(new Date()), []);

    const fetchStatement = useCallback(async () => {
        if (!studentId) return;
        try {
            setLoading(true);
            setError(null);
            const res = await feeService.getStudentMultiYearStatement(studentId);

            // Normalize payload structure
            const statementData = res.data?.student
                ? res.data
                : res.data?.data
                ? res.data.data
                : res.student
                ? res
                : null;

            if (statementData && statementData.student && Array.isArray(statementData.years)) {
                setData(statementData);

                // Open current year by default, and set initial sub-tab to 'academic'
                const initialAccordionState = {};
                const initialTabState = {};

                let currentYr = null;

                statementData.years.forEach((yr) => {
                    initialAccordionState[yr.yearNumber] = yr.isCurrentYear || false;
                    initialTabState[yr.yearNumber] = "academic";
                    if (yr.isCurrentYear) {
                        currentYr = yr.yearNumber;
                    }
                });

                // Fallback: if none is current, pick latest year
                if (!currentYr && statementData.years.length > 0) {
                    const lastYear = statementData.years[statementData.years.length - 1];
                    initialAccordionState[lastYear.yearNumber] = true;
                    currentYr = lastYear.yearNumber;
                }

                setSelectedPrintYear(currentYr || (statementData.years[0] ? statementData.years[0].yearNumber : 1));
                setOpenAccordions(initialAccordionState);
                setActiveTabs(initialTabState);
            } else {
                setError(res.message || "Failed to load statement details.");
            }
        } catch (err) {
            setError(
                err.response?.data?.message || err.message || "Error fetching fee statement"
            );
        } finally {
            setLoading(false);
        }
    }, [studentId]);

    useEffect(() => {
        fetchStatement();
    }, [fetchStatement]);

    const toggleAccordion = (yearNumber) => {
        setOpenAccordions((prev) => ({
            ...prev,
            [yearNumber]: !prev[yearNumber],
        }));
    };

    const setTab = (yearNumber, tabKey) => {
        setActiveTabs((prev) => ({
            ...prev,
            [yearNumber]: tabKey,
        }));
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
        }).format(amount || 0);
    };

    const handlePrintYear = (yrNumber) => {
        setSelectedPrintYear(yrNumber);
        setTimeout(() => {
            window.print();
        }, 100);
    };

    if (loading) {
        return (
            <div className="loading-center" style={{ minHeight: "50vh" }}>
                <div className="spinner" />
                <span>Loading student fee statement…</span>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div style={{ maxWidth: 800, margin: "2rem auto" }}>
                <div
                    style={{
                        padding: "1rem 1.25rem",
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        gap: "0.75rem",
                        color: "#b91c1c",
                        fontSize: 14,
                    }}
                >
                    <FiAlertCircle size={20} style={{ flexShrink: 0 }} />
                    <span>{error || "Student fee statement not found."}</span>
                </div>
                <div style={{ marginTop: "1rem" }}>
                    <button
                        onClick={() => navigate(-1)}
                        className="btn btn-outline"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiArrowLeft size={14} /> Go Back
                    </button>
                </div>
            </div>
        );
    }

    const { student, years } = data;
    const activePrintYearData = years.find((y) => y.yearNumber === selectedPrintYear) || years[0] || null;

    return (
        <div style={{ maxWidth: 1100, margin: "0 auto", paddingBottom: "2rem" }}>
            {/* ── PRINT-SPECIFIC CSS RULES ── */}
            <style>{`
                /* Hide printable statement on screen */
                .fee-statement-print-root {
                    display: none;
                }

                @media print {
                    @page {
                        size: A4 portrait;
                        margin: 10mm 12mm 10mm 12mm;
                    }
                    html, body {
                        height: auto !important;
                        overflow: visible !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #fff !important;
                        font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif !important;
                        color: #1e293b !important;
                    }

                    body * {
                        visibility: hidden !important;
                    }

                    .fee-statement-print-root,
                    .fee-statement-print-root * {
                        visibility: visible !important;
                    }

                    .fee-statement-print-root {
                        display: block !important;
                        position: static !important;
                        width: 100% !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        background: #fff !important;
                    }

                    .fee-statement-document {
                        border: none !important;
                        box-shadow: none !important;
                        padding: 0 !important;
                        width: 100% !important;
                        max-width: 100% !important;
                    }

                    .app-layout,
                    .main-content {
                        display: block !important;
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 100% !important;
                        min-height: auto !important;
                    }

                    .sidebar,
                    .no-print {
                        display: none !important;
                    }
                }
            `}</style>

            {/* ── Screen Interactive Interface ─────────────────────────────── */}
            <div className="no-print">
                {/* ── Toolbar / Header ── */}
                <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem", marginBottom: "1rem" }}>
                    <div>
                        <button
                            onClick={() => navigate(-1)}
                            className="btn btn-outline btn-sm"
                            style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.5rem" }}
                        >
                            <FiArrowLeft size={13} /> Back
                        </button>
                        <h1 className="page-title">Fee Statement — {student.fullName}</h1>
                        <p className="page-subtitle">
                            {student.enrollmentNo ? `Reg No: ${student.enrollmentNo} · ` : ""}
                            {student.courseName} ({student.branchShort}) · Year {student.currentYearRoman}
                        </p>
                    </div>

                    <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                        {years.length > 1 && (
                            <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                                <span style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: 600 }}>Year:</span>
                                <select
                                    className="form-control"
                                    style={{ padding: "4px 8px", fontSize: "13px", minWidth: "140px" }}
                                    value={selectedPrintYear || ""}
                                    onChange={(e) => setSelectedPrintYear(Number(e.target.value))}
                                >
                                    {years.map((y) => (
                                        <option key={y.yearNumber} value={y.yearNumber}>
                                            {y.academicYearLabel} (Year {y.yearRoman})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <button
                            onClick={() => window.print()}
                            className="btn btn-success"
                            style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}
                        >
                            <FiPrinter size={15} /> Print Statement
                        </button>
                    </div>
                </div>

                <div style={{ marginBottom: "1.25rem" }}>
                    <PrintInstructions />
                </div>

                {/* ── Student Profile Header Card ── */}
                <div className="card" style={{ marginBottom: "1.5rem", overflow: "hidden" }}>
                    <div className="card-body" style={{ padding: "1.25rem" }}>
                        <div style={{ display: "flex", gap: "1.25rem", alignItems: "center", flexWrap: "wrap" }}>
                            {/* Avatar */}
                            <div
                                style={{
                                    width: 72,
                                    height: 72,
                                    borderRadius: "50%",
                                    background: "#f1f5f9",
                                    border: "2px solid #cbd5e1",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                    flexShrink: 0,
                                    fontSize: 24,
                                    fontWeight: 700,
                                    color: "#64748b",
                                }}
                            >
                                {student.studentPhoto ? (
                                    <img
                                        src={student.studentPhoto}
                                        alt={student.fullName}
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                ) : (
                                    student.fullName?.charAt(0) || "S"
                                )}
                            </div>

                            {/* Metadata Grid */}
                            <div
                                style={{
                                    flex: 1,
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                    gap: "0.75rem 1.25rem",
                                    fontSize: 13,
                                }}
                            >
                                <div>
                                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", display: "block" }}>
                                        Student Name
                                    </span>
                                    <span style={{ fontWeight: 700, color: "var(--text)" }}>{student.fullName}</span>
                                </div>
                                <div>
                                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", display: "block" }}>
                                        Enrollment / Reg No
                                    </span>
                                    <span style={{ fontWeight: 600 }}>{student.enrollmentNo || "—"}</span>
                                </div>
                                <div>
                                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", display: "block" }}>
                                        Course & Branch
                                    </span>
                                    <span style={{ fontWeight: 600 }}>{student.courseName} ({student.branchShort})</span>
                                </div>
                                <div>
                                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", display: "block" }}>
                                        Current Year
                                    </span>
                                    <span style={{ fontWeight: 600 }}>Year {student.currentYearRoman} · {student.currentAcademicYearLabel}</span>
                                </div>
                                <div>
                                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", display: "block" }}>
                                        Category & Type
                                    </span>
                                    <span style={{ fontWeight: 600 }}>{student.admissionCategory || "General"} · {student.admissionType || "CAP"}</span>
                                </div>
                                <div>
                                    <span style={{ fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", display: "block" }}>
                                        Fees Decided / Scholarship
                                    </span>
                                    <span style={{ fontWeight: 600, color: "#003b67" }}>
                                        {inr(student.feesDecided)} {student.scholarshipAmountBalance > 0 ? `· Sch: ${inr(student.scholarshipAmountBalance)}` : ""}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Year Accordions ── */}
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    {years.map((yr) => {
                        const isOpen = !!openAccordions[yr.yearNumber];
                        const currentTab = activeTabs[yr.yearNumber] || "academic";
                        const yrFeesDecided = Number(yr.feesDecided || student.feesDecided || yr.academic.totalReceivables || 0);
                        const yrPaid = (yr.transactions || []).reduce((sum, tx) => sum + Number(tx.amountPaid || 0), 0) || Number(yr.academic.totalCollected || 0);
                        const yrDue = Math.max(0, yrFeesDecided - yrPaid);

                        return (
                            <div key={yr.yearNumber} className="card" style={{ overflow: "hidden" }}>
                                {/* Accordion Header */}
                                <div
                                    onClick={() => toggleAccordion(yr.yearNumber)}
                                    style={{
                                        padding: "0.85rem 1.25rem",
                                        background: isOpen ? "#f8fafc" : "#fff",
                                        borderBottom: isOpen ? "1px solid var(--border)" : "none",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        cursor: "pointer",
                                        userSelect: "none",
                                        transition: "background 0.15s",
                                        flexWrap: "wrap",
                                        gap: "0.5rem",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                                        <span style={{ fontWeight: 700, fontSize: 15, color: "var(--text)" }}>
                                            {yr.academicYearLabel} · Year {yr.yearRoman}
                                        </span>
                                        {yr.isCurrentYear && (
                                            <span className="badge badge-blue" style={{ fontSize: 11 }}>
                                                Current Year
                                            </span>
                                        )}
                                        {yr.statusBadge && (
                                            <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
                                                {yr.statusBadge}
                                            </span>
                                        )}
                                    </div>

                                    <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                                        <div style={{ fontSize: 13, textAlign: "right" }}>
                                            <span style={{ color: "var(--text-muted)", marginRight: 6 }}>Decided:</span>
                                            <strong style={{ color: "#003b67" }}>{inr(yrFeesDecided)}</strong>
                                            <span style={{ margin: "0 6px", color: "var(--border)" }}>|</span>
                                            <span style={{ color: "var(--text-muted)", marginRight: 6 }}>Paid:</span>
                                            <strong style={{ color: "#16a34a" }}>{inr(yrPaid)}</strong>
                                            <span style={{ margin: "0 6px", color: "var(--border)" }}>|</span>
                                            <span style={{ color: "var(--text-muted)", marginRight: 6 }}>Due:</span>
                                            <strong style={{ color: yrDue > 0 ? "#dc2626" : "#16a34a" }}>
                                                {inr(yrDue)}
                                            </strong>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn btn-outline btn-sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handlePrintYear(yr.yearNumber);
                                            }}
                                            style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", padding: "3px 8px", fontSize: "12px" }}
                                            title="Print this year's statement"
                                        >
                                            <FiPrinter size={12} /> Print
                                        </button>

                                        {isOpen ? <FiChevronUp size={18} color="#64748b" /> : <FiChevronDown size={18} color="#64748b" />}
                                    </div>
                                </div>

                                {/* Accordion Content */}
                                {isOpen && (
                                    <div className="card-body" style={{ padding: "1.25rem" }}>
                                        {/* Sub Tabs Bar */}
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                borderBottom: "1px solid var(--border)",
                                                marginBottom: "1rem",
                                                gap: "0.5rem",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setTab(yr.yearNumber, "academic")}
                                                    style={{
                                                        background: "transparent",
                                                        border: "none",
                                                        borderBottom: currentTab === "academic" ? "2px solid #003b67" : "2px solid transparent",
                                                        color: currentTab === "academic" ? "#003b67" : "var(--text-muted)",
                                                        fontWeight: currentTab === "academic" ? 700 : 500,
                                                        padding: "0.5rem 0.25rem",
                                                        cursor: "pointer",
                                                        fontSize: 13,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.35rem",
                                                    }}
                                                >
                                                    <FiDollarSign size={14} /> Academic Tuition Fees
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setTab(yr.yearNumber, "charges")}
                                                    style={{
                                                        background: "transparent",
                                                        border: "none",
                                                        borderBottom: currentTab === "charges" ? "2px solid #003b67" : "2px solid transparent",
                                                        color: currentTab === "charges" ? "#003b67" : "var(--text-muted)",
                                                        fontWeight: currentTab === "charges" ? 700 : 500,
                                                        padding: "0.5rem 0.25rem",
                                                        cursor: "pointer",
                                                        fontSize: 13,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.35rem",
                                                    }}
                                                >
                                                    <FiShield size={14} color="#0891b2" /> Institutional Charges ({yr.charges?.items?.length || 0})
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setTab(yr.yearNumber, "fines")}
                                                    style={{
                                                        background: "transparent",
                                                        border: "none",
                                                        borderBottom: currentTab === "fines" ? "2px solid #003b67" : "2px solid transparent",
                                                        color: currentTab === "fines" ? "#003b67" : "var(--text-muted)",
                                                        fontWeight: currentTab === "fines" ? 700 : 500,
                                                        padding: "0.5rem 0.25rem",
                                                        cursor: "pointer",
                                                        fontSize: 13,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.35rem",
                                                    }}
                                                >
                                                    <FiAlertCircle size={14} color="#dc2626" /> Fines &amp; Penalties ({yr.fines?.items?.length || 0})
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setTab(yr.yearNumber, "transport")}
                                                    style={{
                                                        background: "transparent",
                                                        border: "none",
                                                        borderBottom: currentTab === "transport" ? "2px solid #003b67" : "2px solid transparent",
                                                        color: currentTab === "transport" ? "#003b67" : "var(--text-muted)",
                                                        fontWeight: currentTab === "transport" ? 700 : 500,
                                                        padding: "0.5rem 0.25rem",
                                                        cursor: "pointer",
                                                        fontSize: 13,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.35rem",
                                                    }}
                                                >
                                                    <FiTruck size={14} /> Bus / Transport {yr.transport?.isSubscribed ? "✓" : ""}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setTab(yr.yearNumber, "hostel")}
                                                    style={{
                                                        background: "transparent",
                                                        border: "none",
                                                        borderBottom: currentTab === "hostel" ? "2px solid #003b67" : "2px solid transparent",
                                                        color: currentTab === "hostel" ? "#003b67" : "var(--text-muted)",
                                                        fontWeight: currentTab === "hostel" ? 700 : 500,
                                                        padding: "0.5rem 0.25rem",
                                                        cursor: "pointer",
                                                        fontSize: 13,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.35rem",
                                                    }}
                                                >
                                                    <FiHome size={14} /> Hostel Accommodation {yr.hostel?.isAllocated ? "✓" : ""}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setTab(yr.yearNumber, "transactions")}
                                                    style={{
                                                        background: "transparent",
                                                        border: "none",
                                                        borderBottom: currentTab === "transactions" ? "2px solid #003b67" : "2px solid transparent",
                                                        color: currentTab === "transactions" ? "#003b67" : "var(--text-muted)",
                                                        fontWeight: currentTab === "transactions" ? 700 : 500,
                                                        padding: "0.5rem 0.25rem",
                                                        cursor: "pointer",
                                                        fontSize: 13,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "0.35rem",
                                                    }}
                                                >
                                                    <FiCreditCard size={14} /> Tuition Receipts ({yr.transactions?.length || 0})
                                                </button>
                                            </div>

                                            {yrDue > 0 && (
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `${feeBasePath}/new`,
                                                            { state: { studentId: student._id, academicYearId: yr.academicYearId } }
                                                        )
                                                    }
                                                    className="btn btn-primary btn-sm"
                                                    style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem" }}
                                                >
                                                    <FiPlus size={13} /> Collect Fee
                                                </button>
                                            )}
                                        </div>

                                        {/* Tab 1: Academic Fees Table */}
                                        {currentTab === "academic" && (
                                            <div className="table-wrapper">
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th style={{ width: 45, textAlign: "center" }}>#</th>
                                                            <th>Fee Head</th>
                                                            <th>Fee Type</th>
                                                            <th style={{ textAlign: "right" }}>Total Receivable</th>
                                                            <th style={{ textAlign: "right" }}>Collected</th>
                                                            <th style={{ textAlign: "right" }}>Outstanding</th>
                                                            <th style={{ textAlign: "center" }}>Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {yr.academic.heads.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={7} style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-muted)" }}>
                                                                    No fee structure configured for this academic year.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            yr.academic.heads.map((headItem) => (
                                                                <tr key={headItem.sNo}>
                                                                    <td style={{ textAlign: "center", color: "var(--text-muted)" }}>
                                                                        {headItem.sNo}
                                                                    </td>
                                                                    <td style={{ fontWeight: 600 }}>{headItem.head}</td>
                                                                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>{headItem.feeType}</td>
                                                                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                                                                        {formatCurrency(headItem.receivables)}
                                                                    </td>
                                                                    <td style={{ textAlign: "right", color: "#16a34a", fontWeight: 600 }}>
                                                                        {formatCurrency(headItem.collected)}
                                                                    </td>
                                                                    <td style={{ textAlign: "right", color: headItem.outstanding > 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>
                                                                        {formatCurrency(headItem.outstanding)}
                                                                    </td>
                                                                    <td style={{ textAlign: "center" }}>
                                                                        {headItem.status === "Paid" ? (
                                                                            <span style={{ background: "#dcfce7", color: "#16a34a", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
                                                                                Paid
                                                                            </span>
                                                                        ) : headItem.status === "Partial" ? (
                                                                            <span style={{ background: "#fef3c7", color: "#d97706", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
                                                                                Partial
                                                                            </span>
                                                                        ) : (
                                                                            <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 600 }}>
                                                                                Unpaid
                                                                            </span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr style={{ background: "#f8fafc", fontWeight: 700 }}>
                                                            <td colSpan={3} style={{ padding: "0.6rem 0.75rem" }}>
                                                                Total Academic Fees (A)
                                                            </td>
                                                            <td style={{ textAlign: "right", padding: "0.6rem 0.75rem" }}>
                                                                {formatCurrency(yr.academic.totalReceivables)}
                                                            </td>
                                                            <td style={{ textAlign: "right", color: "#16a34a", padding: "0.6rem 0.75rem" }}>
                                                                {formatCurrency(yr.academic.totalCollected)}
                                                            </td>
                                                            <td style={{ textAlign: "right", color: "#dc2626", padding: "0.6rem 0.75rem" }}>
                                                                {formatCurrency(yr.academic.totalOutstanding)}
                                                            </td>
                                                            <td />
                                                        </tr>
                                                        {yr.academic.scholarshipAmount > 0 && (
                                                            <tr style={{ background: "#f0fdf4" }}>
                                                                <td colSpan={3} style={{ padding: "0.6rem 0.75rem", color: "#15803d" }}>
                                                                    Scholarship Sanctioned / Disbursed (B)
                                                                </td>
                                                                <td colSpan={3} style={{ textAlign: "right", color: "#15803d", fontWeight: 700, padding: "0.6rem 0.75rem" }}>
                                                                    - {formatCurrency(yr.academic.scholarshipAmount)}
                                                                </td>
                                                                <td />
                                                            </tr>
                                                        )}
                                                        <tr style={{ background: "#f1f5f9", fontWeight: 900, fontSize: 14 }}>
                                                            <td colSpan={3} style={{ padding: "0.75rem" }}>
                                                                Grand Total Payable
                                                            </td>
                                                            <td colSpan={3} style={{ textAlign: "right", padding: "0.75rem" }}>
                                                                {formatCurrency(yr.academic.grandTotal)}
                                                            </td>
                                                            <td />
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        )}

                                        {/* Tab 2: Bus / Transport Facility */}
                                        {currentTab === "transport" && (
                                            <div>
                                                {yr.transport?.isSubscribed ? (
                                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem" }}>
                                                        <div style={{ background: "#e8f1f8", border: "1px solid #b6d5ec", padding: "1rem", borderRadius: "8px" }}>
                                                            <div style={{ fontSize: "11px", fontWeight: 700, color: "#1e40af", textTransform: "uppercase" }}>Bus Route</div>
                                                            <div style={{ fontSize: "15px", fontWeight: 800, color: "#1e293b", marginTop: "3px" }}>{yr.transport.routeName}</div>
                                                            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>Bus No: {yr.transport.busNumber}</div>
                                                        </div>

                                                        <div style={{ background: "#f8fafc", border: "1px solid var(--border)", padding: "1rem", borderRadius: "8px" }}>
                                                            <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase" }}>Boarding Stop &amp; Distance</div>
                                                            <div style={{ fontSize: "15px", fontWeight: 800, color: "#1e293b", marginTop: "3px" }}>{yr.transport.stopName}</div>
                                                            <div style={{ fontSize: "12px", color: "#003b67", marginTop: "2px", fontWeight: 600 }}>{yr.transport.distanceKm} KM ({yr.transport.kilometerRange || "Slab"})</div>
                                                        </div>

                                                        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "1rem", borderRadius: "8px" }}>
                                                            <div style={{ fontSize: "11px", fontWeight: 700, color: "#15803d", textTransform: "uppercase" }}>Monthly Bus Fee</div>
                                                            <div style={{ fontSize: "18px", fontWeight: 900, color: "#15803d", marginTop: "3px" }}>{inr(yr.transport.monthlyFee)}<span style={{ fontSize: "12px", fontWeight: 500 }}>/mo</span></div>
                                                            <div style={{ fontSize: "11.5px", color: "#15803d", marginTop: "2px" }}>Status: Active Passenger</div>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)", background: "#f8fafc", borderRadius: "8px", border: "1px dashed var(--border)" }}>
                                                        Student has not opted for College Bus / Transport facility for this academic session.
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Tab 3: Hostel Accommodation */}
                                        {currentTab === "hostel" && (
                                            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                                                {yr.hostel?.isAllocated ? (
                                                    <div>
                                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1rem" }}>
                                                            <div style={{ background: "#f8fafc", border: "1px solid var(--border)", padding: "10px 14px", borderRadius: "6px" }}>
                                                                <div style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Hostel</div>
                                                                <div style={{ fontWeight: 800, fontSize: "14px" }}>{yr.hostel.hostelName}</div>
                                                            </div>
                                                            <div style={{ background: "#f8fafc", border: "1px solid var(--border)", padding: "10px 14px", borderRadius: "6px" }}>
                                                                <div style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 700 }}>Room &amp; Bed</div>
                                                                <div style={{ fontWeight: 800, fontSize: "14px" }}>Room {yr.hostel.roomNumber} ({yr.hostel.roomType}) · Bed #{yr.hostel.bedNumber}</div>
                                                            </div>
                                                            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "10px 14px", borderRadius: "6px" }}>
                                                                <div style={{ fontSize: "11px", color: "#15803d", textTransform: "uppercase", fontWeight: 700 }}>Total Hostel Paid</div>
                                                                <div style={{ fontWeight: 900, fontSize: "16px", color: "#15803d" }}>{inr(yr.hostel.totalPaid)}</div>
                                                            </div>
                                                        </div>

                                                        {/* Waivers list if any */}
                                                        {yr.hostel.waivers?.length > 0 && (
                                                            <div style={{ background: "#faf5ff", border: "1px solid #e9d5ff", padding: "10px 14px", borderRadius: "6px", marginBottom: "1rem" }}>
                                                                <div style={{ fontSize: "12px", fontWeight: 800, color: "#7c3aed", marginBottom: "4px" }}>
                                                                    ⚖ Excused / Waived Monthly Charges ({yr.hostel.waivers.length}):
                                                                </div>
                                                                {yr.hostel.waivers.map((w, idx) => (
                                                                    <div key={idx} style={{ fontSize: "12px", color: "#6b21a8", marginTop: "2px" }}>
                                                                        • <strong>{w.monthName}</strong>: "{w.waivedReason}" {w.waivedAmount > 0 ? `(${inr(w.waivedAmount)})` : ""}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Receipts */}
                                                        <h4 style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", margin: "0.5rem 0" }}>
                                                            Hostel Payment Receipts
                                                        </h4>
                                                        <div className="table-wrapper">
                                                            <table>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Receipt No</th>
                                                                        <th>Installment</th>
                                                                        <th>Date</th>
                                                                        <th>Mode</th>
                                                                        <th style={{ textAlign: "right" }}>Amount Paid</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {yr.hostel.receipts?.length === 0 ? (
                                                                        <tr>
                                                                            <td colSpan={5} style={{ textAlign: "center", padding: "1rem", color: "var(--text-muted)" }}>
                                                                                No hostel receipts recorded yet.
                                                                            </td>
                                                                        </tr>
                                                                    ) : (
                                                                        yr.hostel.receipts?.map((hr) => (
                                                                            <tr key={hr._id}>
                                                                                <td style={{ fontWeight: 700, color: "#003b67" }}>{hr.receiptNo}</td>
                                                                                <td>{hr.installmentLabel || `Inst #${hr.installmentNo}`}</td>
                                                                                <td>{hr.paymentDate}</td>
                                                                                <td>{hr.paymentMode}</td>
                                                                                <td style={{ textAlign: "right", fontWeight: 800, color: "#16a34a" }}>
                                                                                    {inr(hr.amountPaid)}
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div style={{ padding: "2rem", textAlign: "center", color: "var(--text-muted)", background: "#f8fafc", borderRadius: "8px", border: "1px dashed var(--border)" }}>
                                                        Student has no hostel room allocation for this academic session.
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Tab: Institutional Charges (Uniform, Lab Manual, Books) */}
                                        {currentTab === "charges" && (
                                            <div>
                                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem", marginBottom: "1rem" }}>
                                                    <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "6px", border: "1px solid var(--border)" }}>
                                                        <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>Total Charges Issued</span>
                                                        <div style={{ fontSize: "16px", fontWeight: 800, color: "#1e293b" }}>{inr(yr.charges?.totalIssued || 0)}</div>
                                                    </div>
                                                    <div style={{ background: "#f0fdf4", padding: "10px", borderRadius: "6px", border: "1px solid #bbf7d0" }}>
                                                        <span style={{ fontSize: "11px", color: "#15803d", fontWeight: 700, textTransform: "uppercase" }}>Charges Paid</span>
                                                        <div style={{ fontSize: "16px", fontWeight: 800, color: "#15803d" }}>{inr(yr.charges?.totalPaid || 0)}</div>
                                                    </div>
                                                    <div style={{ background: "#faf5ff", padding: "10px", borderRadius: "6px", border: "1px solid #e9d5ff" }}>
                                                        <span style={{ fontSize: "11px", color: "#7c3aed", fontWeight: 700, textTransform: "uppercase" }}>Charges Waived</span>
                                                        <div style={{ fontSize: "16px", fontWeight: 800, color: "#7c3aed" }}>{inr(yr.charges?.totalWaived || 0)}</div>
                                                    </div>
                                                    <div style={{ background: "#fef2f2", padding: "10px", borderRadius: "6px", border: "1px solid #fecaca" }}>
                                                        <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: 700, textTransform: "uppercase" }}>Balance Due</span>
                                                        <div style={{ fontSize: "16px", fontWeight: 800, color: "#dc2626" }}>{inr(yr.charges?.totalPending || 0)}</div>
                                                    </div>
                                                </div>

                                                <div className="table-wrapper">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Charge Type</th>
                                                                <th>Item Description</th>
                                                                <th style={{ textAlign: "right" }}>Amount</th>
                                                                <th style={{ textAlign: "center" }}>Date</th>
                                                                <th style={{ textAlign: "center" }}>Status</th>
                                                                <th>Payment / Waiver Details</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {!yr.charges?.items || yr.charges.items.length === 0 ? (
                                                                <tr>
                                                                    <td colSpan={6} style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-muted)" }}>
                                                                        No institutional charges (Uniform, Lab Manual, Books) issued for this academic year.
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                yr.charges.items.map((c) => (
                                                                    <tr key={c._id}>
                                                                        <td>
                                                                            <span style={{
                                                                                fontSize: "11px",
                                                                                fontWeight: 700,
                                                                                padding: "2px 7px",
                                                                                borderRadius: "4px",
                                                                                background: "#e8f1f8",
                                                                                color: "#003b67",
                                                                                border: "1px solid #b6d5ec",
                                                                            }}>
                                                                                {c.fineType}
                                                                            </span>
                                                                        </td>
                                                                        <td style={{ fontWeight: 600 }}>{c.title}</td>
                                                                        <td style={{ textAlign: "right", fontWeight: 800, color: c.status === "Paid" ? "#16a34a" : c.status === "Waived" ? "#64748b" : "#dc2626" }}>
                                                                            {inr(c.amount)}
                                                                        </td>
                                                                        <td style={{ textAlign: "center", fontSize: "12px", color: "var(--text-muted)" }}>
                                                                            {c.issuedDate ? new Date(c.issuedDate).toLocaleDateString("en-IN") : "—"}
                                                                        </td>
                                                                        <td style={{ textAlign: "center" }}>
                                                                            {c.status === "Paid" ? (
                                                                                <span style={{ background: "#dcfce7", color: "#16a34a", padding: "2px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 700 }}>
                                                                                    ✓ Paid
                                                                                </span>
                                                                            ) : c.status === "Waived" ? (
                                                                                <span style={{ background: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 700 }}>
                                                                                    ⚖ Waived
                                                                                </span>
                                                                            ) : (
                                                                                <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 700 }}>
                                                                                    ⌛ Pending
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
                                                                            {c.status === "Paid" && c.paymentDetails?.receiptNo ? `${c.paymentDetails.paymentMode || "Cash"} · ${c.paymentDetails.receiptNo}` : c.status === "Waived" && c.waiverReason ? `Waived: "${c.waiverReason}"` : c.remarks || "—"}
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Tab 4: Fines & Penalties */}
                                        {currentTab === "fines" && (
                                            <div>
                                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem", marginBottom: "1rem" }}>
                                                    <div style={{ background: "#f8fafc", padding: "10px", borderRadius: "6px", border: "1px solid var(--border)" }}>
                                                        <span style={{ fontSize: "11px", color: "var(--text-muted)", fontWeight: 700, textTransform: "uppercase" }}>Total Levied</span>
                                                        <div style={{ fontSize: "16px", fontWeight: 800, color: "#1e293b" }}>{inr(yr.fines?.totalIssued || 0)}</div>
                                                    </div>
                                                    <div style={{ background: "#f0fdf4", padding: "10px", borderRadius: "6px", border: "1px solid #bbf7d0" }}>
                                                        <span style={{ fontSize: "11px", color: "#15803d", fontWeight: 700, textTransform: "uppercase" }}>Fines Paid</span>
                                                        <div style={{ fontSize: "16px", fontWeight: 800, color: "#15803d" }}>{inr(yr.fines?.totalPaid || 0)}</div>
                                                    </div>
                                                    <div style={{ background: "#faf5ff", padding: "10px", borderRadius: "6px", border: "1px solid #e9d5ff" }}>
                                                        <span style={{ fontSize: "11px", color: "#7c3aed", fontWeight: 700, textTransform: "uppercase" }}>Fines Waived</span>
                                                        <div style={{ fontSize: "16px", fontWeight: 800, color: "#7c3aed" }}>{inr(yr.fines?.totalWaived || 0)}</div>
                                                    </div>
                                                    <div style={{ background: "#fef2f2", padding: "10px", borderRadius: "6px", border: "1px solid #fecaca" }}>
                                                        <span style={{ fontSize: "11px", color: "#dc2626", fontWeight: 700, textTransform: "uppercase" }}>Balance Pending</span>
                                                        <div style={{ fontSize: "16px", fontWeight: 800, color: "#dc2626" }}>{inr(yr.fines?.totalPending || 0)}</div>
                                                    </div>
                                                </div>

                                                <div className="table-wrapper">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Type</th>
                                                                <th>Reason / Title</th>
                                                                <th style={{ textAlign: "right" }}>Amount</th>
                                                                <th style={{ textAlign: "center" }}>Date</th>
                                                                <th style={{ textAlign: "center" }}>Status</th>
                                                                <th>Details / Remarks</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {!yr.fines?.items || yr.fines.items.length === 0 ? (
                                                                <tr>
                                                                    <td colSpan={6} style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-muted)" }}>
                                                                        No fines or penalties recorded for this student in this academic year.
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                yr.fines.items.map((f) => (
                                                                    <tr key={f._id}>
                                                                        <td>
                                                                            <span style={{
                                                                                fontSize: "11px",
                                                                                fontWeight: 700,
                                                                                padding: "1px 6px",
                                                                                borderRadius: "4px",
                                                                                background: f.fineType === "Lab Fine" ? "#fee2e2" : f.fineType === "Library Fine" ? "#fef3c7" : "#e0e7ff",
                                                                                color: f.fineType === "Lab Fine" ? "#dc2626" : f.fineType === "Library Fine" ? "#d97706" : "#4338ca",
                                                                            }}>
                                                                                {f.fineType}
                                                                            </span>
                                                                        </td>
                                                                        <td style={{ fontWeight: 600 }}>{f.title}</td>
                                                                        <td style={{ textAlign: "right", fontWeight: 800, color: f.status === "Paid" ? "#16a34a" : f.status === "Waived" ? "#64748b" : "#dc2626" }}>
                                                                            {inr(f.amount)}
                                                                        </td>
                                                                        <td style={{ textAlign: "center", fontSize: "12px", color: "var(--text-muted)" }}>
                                                                            {f.issuedDate ? new Date(f.issuedDate).toLocaleDateString("en-IN") : "—"}
                                                                        </td>
                                                                        <td style={{ textAlign: "center" }}>
                                                                            {f.status === "Paid" ? (
                                                                                <span style={{ background: "#dcfce7", color: "#16a34a", padding: "2px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 700 }}>
                                                                                    ✓ Paid
                                                                                </span>
                                                                            ) : f.status === "Waived" ? (
                                                                                <span style={{ background: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 700 }}>
                                                                                    ⚖ Waived
                                                                                </span>
                                                                            ) : (
                                                                                <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 99, fontSize: "11px", fontWeight: 700 }}>
                                                                                    ⌛ Pending
                                                                                </span>
                                                                            )}
                                                                        </td>
                                                                        <td style={{ fontSize: "11.5px", color: "var(--text-muted)" }}>
                                                                            {f.status === "Waived" && f.waiverReason ? `Waived: "${f.waiverReason}"` : f.remarks || "—"}
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}

                                        {/* Tab 3: Receipts / Transaction History */}
                                        {currentTab === "transactions" && (
                                            <div className="table-wrapper">
                                                <table>
                                                    <thead>
                                                        <tr>
                                                            <th>Receipt No</th>
                                                            <th>Installment</th>
                                                            <th>Payment Date</th>
                                                            <th>Mode</th>
                                                            <th>Reference</th>
                                                            <th style={{ textAlign: "right" }}>Amount</th>
                                                            <th style={{ textAlign: "center" }}>Print</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {yr.transactions.length === 0 ? (
                                                            <tr>
                                                                <td colSpan={7} style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-muted)" }}>
                                                                    No fee payment receipts found for this academic session.
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            yr.transactions.map((rcpt) => (
                                                                <tr key={rcpt._id}>
                                                                    <td style={{ fontWeight: 600, color: "#003b67" }}>{rcpt.receiptNo}</td>
                                                                    <td>
                                                                        <div style={{ fontWeight: 600 }}>
                                                                            {rcpt.installmentLabel || `Installment #${rcpt.installmentNo}`}
                                                                        </div>
                                                                        {rcpt.remarks && (
                                                                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                                                                                {rcpt.remarks}
                                                                            </div>
                                                                        )}
                                                                    </td>
                                                                    <td>{rcpt.paymentDate}</td>
                                                                    <td>
                                                                        <span className="badge badge-blue" style={{ fontSize: 11.5 }}>
                                                                            {rcpt.paymentMode}
                                                                        </span>
                                                                    </td>
                                                                    <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                                        {rcpt.transactionRef || rcpt.chequeNo || "—"}
                                                                    </td>
                                                                    <td style={{ textAlign: "right", fontWeight: 700, color: "#16a34a" }}>
                                                                        {formatCurrency(rcpt.amountPaid)}
                                                                    </td>
                                                                    <td style={{ textAlign: "center" }}>
                                                                        <button
                                                                            className="btn btn-outline btn-sm"
                                                                            onClick={() => navigate(`${feeBasePath}/receipts/${rcpt._id}/print`)}
                                                                            title="Print Receipt"
                                                                            style={{ padding: "2px 8px" }}
                                                                        >
                                                                            <FiPrinter size={12} />
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── PRINT AREA (Hidden on Screen, Rendered on Print) ───────────── */}
            <div className="fee-statement-print-root">
                <OfficialStatementSheet
                    student={student}
                    yearData={activePrintYearData}
                    generatedDate={generatedDate}
                />
            </div>
        </div>
    );
}